import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HistoryIcon from '@mui/icons-material/History'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TuneIcon from '@mui/icons-material/Tune'
import AllowanceBar from '../../kit/AllowanceBar'
import { SoftChip } from '../../kit/Chips'
import KeyValueList from '../../kit/KeyValueList'
import SectionCard from '../../kit/SectionCard'
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPeriod,
  initials,
} from '../../kit/helpers'
import { ALLOWANCE_METRICS } from '../tenantHelpers'
import { usageParts } from './ActionDialog'
import {
  EMAIL_STAGES,
  FALLBACK_ALLOWED_ACTIONS,
  GRANT_STATUS,
  INCLUDED_SOURCES,
  METRIC_KINDS,
  METRIC_LABELS,
  METRIC_NAMES,
  NOT_MONITORED_REASONS,
  QUOTE_METRICS,
  RECORD_ACTIONS,
  metricKind,
  ROOM_FLAGS,
  STRIPE_CHANGE_LABELS,
  VARIATION_KINDS,
  actionStatusMeta,
  formatHours,
  grantAffectedPeriods,
  historyActionMeta,
  isHttpUrl,
  notMonitoredLabel,
  recordActionLabel,
  sequenceStatusMeta,
} from './constants'

// The read-only sections of the Commercial usage tab. Every figure shown is
// taken from the panel payload as the server sent it: periods, occurrence
// numbers, allowances and the actions a month allows are never worked out
// here. `onOpen(dialog, details)` asks the panel to open one of its dialogs.

const headCellSx = {
  color: '#64748B',
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  bgcolor: '#F8FAFC',
  borderColor: '#EEF2F7',
  whiteSpace: 'nowrap',
}

const cellSx = { borderColor: '#EEF2F7', verticalAlign: 'top' }

// The monthly table has nine columns: tighter side padding keeps them all in
// view on a desktop-width card.
const monthlyTableSx = { minWidth: 960, '& th, & td': { px: 1.25 }, '& th:first-of-type, & td:first-of-type': { pl: 2 } }

const Empty = ({ children }) => (
  <Typography variant="body2" color="text.secondary">
    {children}
  </Typography>
)

const SubHeading = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      color: 'text.secondary',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    }}
  >
    {children}
  </Typography>
)

const isVoided = (record) => !!record?.voidedAt || record?.action?.status === 'voided'

const openRecordAction = (onOpen, key, target) =>
  onOpen(RECORD_ACTIONS[key].dialog, { target, period: target.period })

// ---------------------------------------------------------------------------
// 1. Required action
// ---------------------------------------------------------------------------

// A finalised month whose live recalculation no longer matches the figures it
// was finalised on - the cue that "Reset after correction" may be needed.
const DriftWarning = ({ drift }) => {
  const now = []
  const recorded = []
  if (drift.ptsCount !== drift.storedPtsCount) {
    now.push(`${formatNumber(drift.ptsCount)} PTS journeys`)
    recorded.push(`${formatNumber(drift.storedPtsCount)} PTS journeys`)
  }
  if (drift.controlRoomMinutes !== drift.storedControlRoomMinutes) {
    now.push(`${formatHours(drift.controlRoomMinutes)} control-room hours`)
    recorded.push(`${formatHours(drift.storedControlRoomMinutes)} control-room hours`)
  }
  const stillExceeded = drift.stillExceeded ?? []
  return (
    <Alert severity="warning" sx={{ mt: 1.25 }}>
      Current data now shows {now.join(' and ')} for this month; the recorded figure was{' '}
      {recorded.join(' and ')}.{' '}
      {stillExceeded.length > 0
        ? `On the current data the month would still be above its allowance for ${stillExceeded.map((metric) => METRIC_NAMES[metric] ?? metric).join(' and ')}.`
        : 'On the current data the month would no longer be above its allowance.'}{' '}
      The recorded figure stands unless it was a data error - if it was, use Reset after
      correction.
    </Alert>
  )
}

const DraftButtons = ({ stages = [], target, onOpen }) =>
  stages.map((stage) => (
    <Button
      key={stage}
      size="small"
      variant="outlined"
      startIcon={<MailOutlineIcon />}
      onClick={() => onOpen('draft', { period: target.period, stage })}
    >
      {EMAIL_STAGES[stage]?.button ?? `Draft ${stage} email`}
    </Button>
  ))

// One month that needs something doing: what it exceeded, what staff are
// asked to do, and the actions the server allows for it.
const RequiredActionItem = ({ required, record, drift, onOpen }) => {
  const meta = actionStatusMeta(required.status)
  const high = required.priority === 'high'
  const target = record ?? required
  const allowed = record ? (record.allowedActions ?? []) : (FALLBACK_ALLOWED_ACTIONS[required.status] ?? [])
  const primary = required.status === 'advisoryRequired' ? 'advisory-sent' : 'quote-sent'
  const upgrade = required.status === 'upgradeRequired'

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: high ? alpha('#E32525', 0.35) : '#E2E8F0',
        borderLeft: `4px solid ${meta.color}`,
        borderRadius: 2,
        bgcolor: high ? alpha('#E32525', 0.03) : '#FFFFFF',
        p: 1.5,
      }}
    >
      <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
        <Typography fontWeight={700}>{formatPeriod(required.period, 'MMMM YYYY')}</Typography>
        <SoftChip color={meta.color} label={meta.label} />
        {required.occurrenceNumber && (
          <SoftChip color="#64748B" label={`Occurrence ${required.occurrenceNumber}`} />
        )}
        {high && <SoftChip color="#E32525" icon={<PriorityHighIcon />} label="High priority" />}
      </Stack>
      <Typography variant="body2" fontWeight={600} marginTop={0.75}>
        {usageParts(target).join(' · ')}
      </Typography>
      {meta.todo && (
        <Typography variant="body2" color="text.secondary" marginTop={0.25}>
          {meta.todo}
        </Typography>
      )}
      {!record && (
        <Typography variant="caption" color="text.secondary" display="block" marginTop={0.5}>
          This month is older than the months listed below, so its figures are not shown here.
        </Typography>
      )}
      {drift && <DriftWarning drift={drift} />}
      <Stack direction="row" gap={1} flexWrap="wrap" marginTop={1.25}>
        {upgrade && (
          <Button size="small" variant="contained" onClick={() => onOpen('transition', {})}>
            Complete Enterprise+ transition
          </Button>
        )}
        {allowed.includes(primary) && (
          <Button
            size="small"
            variant={upgrade ? 'outlined' : 'contained'}
            onClick={() => openRecordAction(onOpen, primary, target)}
          >
            {recordActionLabel(primary, required.status)}
          </Button>
        )}
        {allowed.includes('seasonal-review') && (
          <Button size="small" variant="outlined" onClick={() => openRecordAction(onOpen, 'seasonal-review', target)}>
            {recordActionLabel('seasonal-review')}
          </Button>
        )}
        <DraftButtons stages={record?.emailDraftStages} target={target} onOpen={onOpen} />
        {allowed.includes('reset') && (
          <Button size="small" color="error" onClick={() => openRecordAction(onOpen, 'reset', target)}>
            {recordActionLabel('reset')}
          </Button>
        )}
      </Stack>
    </Box>
  )
}

// A seasonal review has been recorded but the customer has not yet been
// recorded as told their usage.
const FollowUpItem = ({ record, onOpen }) => {
  const review = record.action?.seasonalReview ?? {}
  return (
    <Box sx={{ border: '1px solid #E2E8F0', borderLeft: '4px solid #268EBB', borderRadius: 2, p: 1.5 }}>
      <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
        <Typography fontWeight={700}>{formatPeriod(record.period, 'MMMM YYYY')}</Typography>
        <SoftChip color="#268EBB" label="Seasonal review - no change" />
        <SoftChip color="#F28B1F" label="Customer not yet told" />
      </Stack>
      <Typography variant="body2" color="text.secondary" marginTop={0.75}>
        Decided {formatDate(review.decidedAt)}
        {review.decidedByName ? ` by ${review.decidedByName}` : ''}
        {review.rationale ? ` - ${review.rationale}` : ''}. The customer is still told their usage
        for the month: send the seasonal review email, then record it.
      </Typography>
      <Stack direction="row" gap={1} flexWrap="wrap" marginTop={1.25}>
        <Button size="small" variant="contained" onClick={() => openRecordAction(onOpen, 'customer-informed', record)}>
          {recordActionLabel('customer-informed')}
        </Button>
        <DraftButtons stages={record.emailDraftStages} target={record} onOpen={onOpen} />
      </Stack>
    </Box>
  )
}

export const RequiredActionCard = ({ panel, onOpen }) => {
  const sequence = panel.sequence ?? {}
  const monitoring = panel.monitoring ?? {}
  const records = panel.records ?? []
  const requiredActions = panel.requiredActions ?? []
  const status = sequenceStatusMeta(sequence.status)
  const recordFor = (period) => records.find((record) => record.period === period)
  const followUps = records.filter((record) => (record.allowedActions ?? []).includes('customer-informed'))
  const exceededPeriods = sequence.exceededPeriods ?? []
  // A reset needs something to reset: a month the server allows it for, or an
  // active sequence.
  const canReset =
    exceededPeriods.length > 0 || records.some((record) => (record.allowedActions ?? []).includes('reset'))

  return (
    <SectionCard
      title="Required action"
      icon={<AssignmentLateIcon />}
      subtitle="Manual commercial review - nothing is emailed, charged or changed automatically"
      action={
        <Button size="small" color="error" onClick={() => onOpen('reset', {})} disabled={!canReset}>
          Reset after correction
        </Button>
      }
    >
      <Stack gap={2}>
        <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
          <SoftChip color={status.color} label={status.label} />
          <SoftChip
            color={monitoring.monitored ? '#00964C' : '#64748B'}
            label={monitoring.monitored ? 'Monitored' : `Not monitored - ${notMonitoredLabel(monitoring.reason)}`}
          />
        </Stack>
        {!monitoring.monitored && (
          <Alert severity="info">
            {NOT_MONITORED_REASONS[monitoring.reason]?.detail ??
              'This month is not being assessed against the allowances.'}{' '}
            Usage is still recorded below.
          </Alert>
        )}
        <KeyValueList
          columns={4}
          showEmpty
          rows={[
            {
              label: 'Current occurrence',
              value: sequence.currentOccurrenceCount > 0 ? `Occurrence ${sequence.currentOccurrenceCount}` : 'None',
            },
            { label: 'Last exceeded month', value: formatPeriod(sequence.lastExceededPeriod) },
            {
              label: 'Exceeded months in the active sequence',
              value: exceededPeriods.map((period) => formatPeriod(period)).join(', '),
            },
            {
              label: 'Sequence resets after',
              value: sequence.resetAfterMonths
                ? `${sequence.resetAfterMonths} months without an exceeded month`
                : '',
            },
            sequence.sequenceStartPeriod
              ? {
                  label: 'Sequence counted from',
                  value: `${formatPeriod(sequence.sequenceStartPeriod)} (after a reset or an Enterprise+ transition)`,
                  fullWidth: true,
                }
              : null,
          ]}
        />
        <Divider sx={{ borderColor: '#EEF2F7' }} />
        {requiredActions.length === 0 && followUps.length === 0 ? (
          <Stack direction="row" gap={1} alignItems="center" color="text.secondary">
            <CheckCircleOutlineIcon sx={{ color: 'anGreen.main' }} />
            <Typography variant="body2">No action is required right now.</Typography>
          </Stack>
        ) : (
          <Stack gap={1.5}>
            {requiredActions.map((required) => (
              <RequiredActionItem
                key={required.period}
                required={required}
                record={recordFor(required.period)}
                drift={panel.drift?.[required.period]}
                onOpen={onOpen}
              />
            ))}
            {followUps.map((record) => (
              <FollowUpItem key={record.period} record={record} onOpen={onOpen} />
            ))}
          </Stack>
        )}
      </Stack>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// 2. Effective allowances
// ---------------------------------------------------------------------------

const figure = (value, prefix = '') =>
  typeof value === 'number' && value > 0 ? `${prefix}${formatNumber(value)}` : '—'

export const AllowancesCard = ({ panel }) => {
  const allowances = panel.allowances ?? {}
  return (
    <SectionCard
      title="Effective allowances"
      icon={<TuneIcon />}
      subtitle="Included + additional + complimentary capacity (+ seats for employees)"
      dense
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Allowance</TableCell>
              <TableCell sx={headCellSx} align="right">Included</TableCell>
              <TableCell sx={headCellSx} align="right">Additional</TableCell>
              <TableCell sx={headCellSx} align="right">Complimentary</TableCell>
              <TableCell sx={headCellSx} align="right">Seats</TableCell>
              <TableCell sx={headCellSx} align="right">Effective</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ALLOWANCE_METRICS.map((metric) => {
              const allowance = allowances[metric.key] ?? {}
              const kindKey = metricKind(metric.key)
              const kind = METRIC_KINDS[kindKey] ?? METRIC_KINDS.recorded
              const seats = [
                allowance.purchasedSeats > 0 ? `+${formatNumber(allowance.purchasedSeats)} purchased` : '',
                allowance.complimentarySeats > 0 ? `+${formatNumber(allowance.complimentarySeats)} free` : '',
              ].filter(Boolean)
              const effective =
                typeof allowance.effective === 'number'
                  ? formatNumber(allowance.effective)
                  : kindKey === 'hard' && metric.key !== 'concurrentControlRooms'
                    ? 'No limit'
                    : 'Not included'
              return (
                <TableRow key={metric.key} sx={{ '&:last-of-type td': { borderBottom: 0 } }}>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2" fontWeight={600}>
                      {metric.label}
                    </Typography>
                    <Tooltip describeChild title={kind.hint}>
                      <span>
                        <SoftChip color={kind.color} label={kind.label} sx={{ height: 20, fontSize: 11, mt: 0.25 }} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Typography variant="body2">
                      {typeof allowance.included === 'number' ? formatNumber(allowance.included) : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'nowrap' }}>
                      {INCLUDED_SOURCES[allowance.includedSource] ?? allowance.includedSource ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Typography variant="body2">{figure(allowance.additional, '+')}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Typography variant="body2">{figure(allowance.grants, '+')}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Typography variant="body2">{seats.length ? seats.join(', ') : '—'}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Typography variant="body2" fontWeight={700}>
                      {effective}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 2, py: 1.25, borderTop: '1px solid #EEF2F7' }}>
        Capacity limits (including concurrent control rooms, checked when a room is started or
        reopened) are as enforced now. The monthly allowances are for{' '}
        {formatPeriod(panel.currentPeriod, 'MMMM YYYY')}: complimentary capacity counts in full for
        every month it overlaps. Included and additional limits are edited on the Contract &amp;
        billing tab.
      </Typography>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// 3. Monthly usage
// ---------------------------------------------------------------------------

const recordState = (record) => {
  if (isVoided(record)) {
    return {
      label: 'Voided',
      color: '#64748B',
      hint: `Reset after correction ${formatDate(record.voidedAt)}${record.voidedByName ? ` by ${record.voidedByName}` : ''}${record.voidedReason ? ` - ${record.voidedReason}` : ''}`,
    }
  }
  if (record.finalisedAt) {
    return { label: 'Finalised', color: '#268EBB', hint: `Finalised ${formatDateTime(record.finalisedAt)}. The figures no longer change.` }
  }
  if (record.live) {
    return { label: 'Live (provisional)', color: '#239AA4', hint: 'Calculated just now from the tenant\'s journeys and control rooms. Still changing until the month is finalised.' }
  }
  return {
    label: 'Open (stored figures)',
    color: '#64748B',
    hint: `Not finalised yet. Live figures were unavailable, so these are from ${record.lastCalculatedAt ? formatDateTime(record.lastCalculatedAt) : 'the last nightly run'}.`,
  }
}

// What was recorded against the month's action, in one line.
const actionDetail = (record) => {
  const action = record.action ?? {}
  const by = (name) => (name ? ` by ${name}` : '')
  const parts = []
  if (isVoided(record)) {
    parts.push(`Reset ${formatDate(record.voidedAt)}${by(record.voidedByName)}`)
  } else {
    if (action.advisorySentAt) parts.push(`Advisory sent ${formatDate(action.advisorySentAt)}${by(action.advisorySentByName)}`)
    if (action.quoteSentAt) {
      parts.push(
        `${['Quote', action.quoteReference, 'sent', formatDate(action.quoteSentAt)].filter(Boolean).join(' ')}${by(action.quoteSentByName)}`,
      )
    }
    if (action.seasonalReview?.decidedAt) {
      parts.push(
        `Decided ${formatDate(action.seasonalReview.decidedAt)}${by(action.seasonalReview.decidedByName)}`,
        action.seasonalReview.customerInformedAt
          ? `customer told ${formatDate(action.seasonalReview.customerInformedAt)}${by(action.seasonalReview.customerInformedByName)}`
          : 'customer not yet told',
      )
    }
    if (action.upgradeCompletedAt) {
      parts.push(`Upgrade completed ${formatDate(action.upgradeCompletedAt)}${by(action.upgradeCompletedByName)}`)
    }
  }
  return parts.join(' · ')
}

// "2 long-open" and "3 flagged rooms" chips. Review information only.
const ReviewFlags = ({ record }) => {
  const longOpen = record.controlRoom?.longOpenRoomFlags?.length ?? 0
  const flagged = (record.controlRoom?.roomFlags ?? []).filter((room) => room.flags?.length)
  if (!longOpen && !flagged.length) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    )
  }
  const counts = {}
  for (const room of flagged) for (const flag of room.flags) counts[flag] = (counts[flag] ?? 0) + 1
  const flagSummary = Object.entries(counts)
    .map(([flag, count]) => `${ROOM_FLAGS[flag] ?? flag}${count > 1 ? ` ×${count}` : ''}`)
    .join(', ')
  return (
    <Stack gap={0.5} alignItems="flex-start">
      {longOpen > 0 && (
        <Tooltip describeChild title="Control rooms open for more than 48 hours in one stretch. Review information only - no room is closed and no time is removed.">
          <span>
            <SoftChip color="#64748B" label={`${longOpen} long-open room${longOpen === 1 ? '' : 's'}`} sx={{ height: 20, fontSize: 11 }} />
          </span>
        </Tooltip>
      )}
      {flagged.length > 0 && (
        <Tooltip describeChild title={`Data-quality flags: ${flagSummary}. Review information only.`}>
          <span>
            <SoftChip color="#64748B" label={`${flagged.length} flagged room${flagged.length === 1 ? '' : 's'}`} sx={{ height: 20, fontSize: 11 }} />
          </span>
        </Tooltip>
      )}
    </Stack>
  )
}

const UsageFigure = ({ used, allowance, exceeded, counted }) => (
  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
    <Box component="span" sx={{ fontWeight: exceeded ? 700 : 500, color: exceeded && counted ? '#B54708' : 'text.primary' }}>
      {used}
    </Box>
    <Box component="span" color="text.secondary">
      {' '}
      / {allowance ?? 'not included'}
    </Box>
  </Typography>
)

export const MonthlyUsageCard = ({ panel, onOpen }) => {
  const records = panel.records ?? []
  // The record is kept while the menu closes so its items do not vanish
  // mid-transition.
  const [menu, setMenu] = useState({ anchor: null, record: null })
  const current = records.find((record) => record.period === panel.currentPeriod)
  const currentNote = current?.monitored === false ? `Not monitored - ${notMonitoredLabel(current.notMonitoredReason)}` : undefined

  const closeMenu = () => setMenu((prev) => ({ ...prev, anchor: null }))
  const menuRecord = menu.record
  const menuStatus = menuRecord?.action?.status

  return (
    <SectionCard
      title="Monthly usage"
      icon={<CalendarMonthIcon />}
      subtitle="Calendar months, Europe/London - newest first"
      dense
    >
      {current && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #EEF2F7' }}>
          <SubHeading>
            {formatPeriod(current.period, 'MMMM YYYY')} so far -{' '}
            {current.live ? 'live figures, provisional' : 'stored figures'}
          </SubHeading>
          <Grid container spacing={3} marginTop={0}>
            <Grid item xs={12} sm={6} lg={4}>
              <AllowanceBar
                label="PTS journeys"
                used={current.pts?.count ?? 0}
                allowance={current.pts?.allowanceSnapshot}
                usedText={formatNumber(current.pts?.count ?? 0)}
                allowanceText={formatNumber(current.pts?.allowanceSnapshot)}
                caption={currentNote}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <AllowanceBar
                label="Control-room hours"
                used={current.controlRoom?.minutes ?? 0}
                allowance={current.controlRoom?.allowanceMinutesSnapshot}
                usedText={formatHours(current.controlRoom?.minutes ?? 0)}
                allowanceText={formatHours(current.controlRoom?.allowanceMinutesSnapshot)}
                caption={currentNote}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
                <Typography variant="body2" fontWeight={600}>
                  Peak concurrent rooms
                </Typography>
                <Typography variant="body2">
                  <Box component="span" fontWeight={700}>
                    {formatNumber(current.controlRoom?.peakConcurrentRooms ?? 0)}
                  </Box>{' '}
                  <Box component="span" color="text.secondary">
                    {typeof current.controlRoom?.concurrentRoomsAllowanceSnapshot === 'number'
                      ? `· agreed ${formatNumber(current.controlRoom.concurrentRoomsAllowanceSnapshot)}`
                      : '· none agreed'}
                  </Box>
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" marginTop={0.5}>
                Enforced capacity at start / reopen - never an exceeded month
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
      {records.length === 0 ? (
        <Box p={2}>
          <Empty>No usage has been recorded for this tenant yet. Records are created by the nightly reconciliation.</Empty>
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={monthlyTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Month</TableCell>
                <TableCell sx={headCellSx}>PTS journeys</TableCell>
                <TableCell sx={headCellSx}>Control-room hours</TableCell>
                <TableCell sx={headCellSx}>Peak rooms</TableCell>
                <TableCell sx={headCellSx}>Review flags</TableCell>
                <TableCell sx={headCellSx}>Exceeded</TableCell>
                <TableCell sx={headCellSx}>Action</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => {
                const state = recordState(record)
                const voided = isVoided(record)
                const status = record.action?.status ?? 'none'
                const meta = actionStatusMeta(status)
                const exceeded = record.exceededMetrics ?? []
                const counted = record.monitored !== false && !voided
                const hasMenu = (record.allowedActions ?? []).length > 0 || (record.emailDraftStages ?? []).length > 0
                const detail = actionDetail(record)
                const agreedRooms = record.controlRoom?.concurrentRoomsAllowanceSnapshot
                return (
                  <TableRow key={record.period} sx={{ '&:last-of-type td': { borderBottom: 0 }, opacity: voided ? 0.7 : 1 }}>
                    <TableCell sx={cellSx}>
                      <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                        {formatPeriod(record.period)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <UsageFigure
                        used={formatNumber(record.pts?.count ?? 0)}
                        allowance={typeof record.pts?.allowanceSnapshot === 'number' ? formatNumber(record.pts.allowanceSnapshot) : null}
                        exceeded={exceeded.includes('ptsJourneysPerMonth')}
                        counted={counted}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <UsageFigure
                        used={formatHours(record.controlRoom?.minutes ?? 0)}
                        allowance={typeof record.controlRoom?.allowanceMinutesSnapshot === 'number' ? formatHours(record.controlRoom.allowanceMinutesSnapshot) : null}
                        exceeded={exceeded.includes('controlRoomHoursPerMonth')}
                        counted={counted}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Tooltip describeChild title="Peak concurrent control rooms against the agreed number. Concurrency is enforced capacity when a room is started or reopened, and is never counted as an exceeded month.">
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          <Box component="span" fontWeight={500}>
                            {formatNumber(record.controlRoom?.peakConcurrentRooms ?? 0)}
                          </Box>
                          <Box component="span" color="text.secondary">
                            {' '}
                            / {typeof agreedRooms === 'number' ? formatNumber(agreedRooms) : 'none agreed'}
                          </Box>
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <ReviewFlags record={record} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {exceeded.length === 0 ? (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      ) : (
                        <Stack gap={0.5} alignItems="flex-start">
                          {record.occurrenceNumber && (
                            <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                              Occurrence {record.occurrenceNumber}
                            </Typography>
                          )}
                          {exceeded.map((metric) => (
                            <Tooltip
                              key={metric}
                              describeChild
                              title={counted ? '' : 'Over the allowance figure, but the month is not counted (not monitored, or reset).'}
                            >
                              <span>
                                <SoftChip
                                  color={counted ? '#F28B1F' : '#64748B'}
                                  label={METRIC_NAMES[metric] ?? metric}
                                  sx={{ height: 20, fontSize: 11 }}
                                />
                              </span>
                            </Tooltip>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 240 }}>
                      {status === 'none' ? (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      ) : (
                        <SoftChip color={meta.color} label={meta.label} sx={{ height: 20, fontSize: 11 }} />
                      )}
                      {record.action?.priority === 'high' && !voided && (
                        <SoftChip color="#E32525" label="High priority" sx={{ height: 20, fontSize: 11, ml: 0.5 }} />
                      )}
                      {detail && (
                        <Typography variant="caption" color="text.secondary" display="block" marginTop={0.25}>
                          {detail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Tooltip describeChild title={state.hint}>
                        <span>
                          <SoftChip color={state.color} label={state.label} sx={{ height: 20, fontSize: 11 }} />
                        </span>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary" display="block" marginTop={0.25}>
                        {voided
                          ? 'No longer counted'
                          : record.monitored === false
                            ? `Not monitored - ${notMonitoredLabel(record.notMonitoredReason)}`
                            : 'Monitored'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align="right" padding="none">
                      {hasMenu && (
                        <IconButton
                          size="small"
                          aria-label={`Actions for ${formatPeriod(record.period, 'MMMM YYYY')}`}
                          onClick={(event) => setMenu({ anchor: event.currentTarget, record })}
                          sx={{ mt: 0.5, mr: 0.5 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 2, py: 1.25, borderTop: '1px solid #EEF2F7' }}>
        Only PTS journeys and control-room hours (recorded for review only) can make an exceeded
        month. Concurrent control rooms are enforced capacity when a room is started or reopened,
        and are never counted as an exceeded month. Long-open rooms and data-quality flags are
        review information only: nothing is closed, removed or charged because of them. Live months are provisional until they are finalised after month end; a finalised
        month&apos;s figures never change.
      </Typography>

      <Menu anchorEl={menu.anchor} open={!!menu.anchor} onClose={closeMenu}>
        {(menuRecord?.allowedActions ?? [])
          .filter((key) => RECORD_ACTIONS[key])
          .map((key) => (
            <MenuItem
              key={key}
              sx={RECORD_ACTIONS[key].destructive ? { color: 'error.main' } : undefined}
              onClick={() => {
                closeMenu()
                openRecordAction(onOpen, key, menuRecord)
              }}
            >
              {recordActionLabel(key, menuStatus)}
            </MenuItem>
          ))}
        {(menuRecord?.emailDraftStages ?? []).map((stage) => (
          <MenuItem
            key={stage}
            onClick={() => {
              closeMenu()
              onOpen('draft', { period: menuRecord.period, stage })
            }}
          >
            {EMAIL_STAGES[stage]?.button ?? `Draft ${stage} email`}
          </MenuItem>
        ))}
      </Menu>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// 5. Quote
// ---------------------------------------------------------------------------

export const QuoteCard = ({ panel }) => {
  const quoted = (panel.records ?? []).filter((record) => record.action?.quoteReference)
  return (
    <SectionCard
      title="Quote"
      icon={<RequestQuoteIcon />}
      subtitle="Enterprise+ quotations recorded against a month"
    >
      {quoted.length === 0 ? (
        <Empty>No quote has been recorded.</Empty>
      ) : (
        <Stack gap={2} divider={<Divider sx={{ borderColor: '#EEF2F7' }} />}>
          {quoted.map((record) => {
            const action = record.action
            const allowance = QUOTE_METRICS.filter((metric) => typeof action.quoteAllowance?.[metric] === 'number')
              .map((metric) => `${METRIC_LABELS[metric]}: ${formatNumber(action.quoteAllowance[metric])}`)
              .join(' · ')
            return (
              <Stack gap={1} key={record.period}>
                <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
                  <Typography variant="body2" fontWeight={700}>
                    {formatPeriod(record.period, 'MMMM YYYY')}
                  </Typography>
                  {record.occurrenceNumber && (
                    <SoftChip color="#64748B" label={`Occurrence ${record.occurrenceNumber}`} sx={{ height: 20, fontSize: 11 }} />
                  )}
                  {isVoided(record) && <SoftChip color="#64748B" label="Month voided" sx={{ height: 20, fontSize: 11 }} />}
                </Stack>
                <KeyValueList
                  columns={3}
                  showEmpty
                  rows={[
                    { label: 'Reference', value: action.quoteReference },
                    { label: 'Proposed package', value: action.quotePackage },
                    { label: 'Fee', value: formatCurrency(action.quoteAmount, action.quoteCurrency) },
                    { label: 'Valid until', value: formatDate(action.quoteValidUntil) },
                    {
                      label: 'Sent',
                      value: action.quoteSentAt
                        ? `${formatDate(action.quoteSentAt)}${action.quoteSentByName ? ` by ${action.quoteSentByName}` : ''}`
                        : '',
                    },
                    {
                      label: 'Link',
                      value: action.quoteLink,
                      render:
                        action.quoteLink && isHttpUrl(action.quoteLink) ? (
                          <Link
                            href={action.quoteLink}
                            target="_blank"
                            rel="noreferrer"
                            variant="body2"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, wordBreak: 'break-all' }}
                          >
                            Open quote
                            <OpenInNewIcon sx={{ fontSize: 15 }} />
                          </Link>
                        ) : null,
                    },
                    { label: 'Proposed allowances', value: allowance, fullWidth: true },
                    action.notes ? { label: 'Notes', value: action.notes, fullWidth: true } : null,
                  ]}
                />
              </Stack>
            )
          })}
        </Stack>
      )}
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// 6. Complimentary capacity
// ---------------------------------------------------------------------------

export const GrantsCard = ({ panel, onOpen }) => {
  const grants = panel.grants ?? []
  return (
    <SectionCard
      title="Complimentary capacity"
      icon={<CardGiftcardIcon />}
      subtitle="Temporary capacity granted by AmbuNet - always with an expiry"
      action={
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => onOpen('grant', {})}>
          Add
        </Button>
      }
    >
      {grants.length === 0 ? (
        <Empty>No complimentary capacity has been granted.</Empty>
      ) : (
        <Stack gap={1.5} divider={<Divider sx={{ borderColor: '#EEF2F7' }} />}>
          {grants.map((grant) => {
            const status = GRANT_STATUS[grant.status] ?? { label: grant.status, color: '#64748B' }
            // The months the grant can actually change when the server says so
            // (finalised months are never altered), else every month it overlaps.
            const affected = grantAffectedPeriods(grant)
            const periods = affected ?? grant.appliesToPeriods ?? []
            const unaffected = affected
              ? (grant.appliesToPeriods ?? []).filter((period) => !affected.includes(period))
              : []
            return (
              <Stack gap={0.5} key={String(grant._id)}>
                <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" fontWeight={700}>
                    +{formatNumber(grant.amount)} {METRIC_LABELS[grant.metric] ?? grant.metric}
                  </Typography>
                  <SoftChip color={status.color} label={status.label} sx={{ height: 20, fontSize: 11 }} />
                  <Box flex={1} />
                  {['active', 'scheduled'].includes(grant.status) && (
                    <Button size="small" color="error" onClick={() => onOpen('revoke', { grant })}>
                      Revoke
                    </Button>
                  )}
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {grant.reason || 'No reason recorded'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Starts {formatDate(grant.startsAt ?? grant.grantedAt)} · expires {formatDate(grant.expiresAt)} (that day not included)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Applies to:{' '}
                  {periods.length
                    ? periods.map((period) => formatPeriod(period)).join(', ')
                    : affected
                      ? 'no month still open'
                      : '—'}
                  {affected
                    ? ` · finalised months are unaffected${unaffected.length ? ` (${unaffected.map((period) => formatPeriod(period)).join(', ')})` : ''}`
                    : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Granted {formatDate(grant.grantedAt)}
                  {grant.grantedByName ? ` by ${grant.grantedByName}` : ''}
                </Typography>
                {grant.revokedAt && (
                  <Typography variant="caption" display="block" sx={{ color: '#B42318' }}>
                    Revoked {formatDate(grant.revokedAt)}
                    {grant.revokedByName ? ` by ${grant.revokedByName}` : ''}
                    {grant.revokedReason ? ` - ${grant.revokedReason}` : ''}
                  </Typography>
                )}
              </Stack>
            )
          })}
        </Stack>
      )}
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// 7. Enterprise+ upgrade and contract variations
// ---------------------------------------------------------------------------

const VISIBLE_VARIATIONS = 5

export const UpgradeCard = ({ panel, onOpen }) => {
  const upgrade = panel.upgrade
  const variations = panel.variations ?? []
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? variations : variations.slice(0, VISIBLE_VARIATIONS)
  const upgradeRequired = (panel.requiredActions ?? []).some((action) => action.status === 'upgradeRequired')

  return (
    <SectionCard
      title="Enterprise+ upgrade"
      icon={<TrendingUpIcon />}
      subtitle="The agreed contract variation - Stripe is changed by hand"
      action={
        <Button
          size="small"
          variant={upgradeRequired ? 'contained' : 'outlined'}
          onClick={() => onOpen('transition', {})}
        >
          Complete transition
        </Button>
      }
    >
      <Stack gap={2}>
        {upgrade ? (
          <KeyValueList
            showEmpty
            rows={[
              {
                label: 'Completed',
                value: `${formatDateTime(upgrade.completedAt)}${upgrade.completedByName ? ` by ${upgrade.completedByName}` : ''}`,
                fullWidth: true,
              },
              { label: 'Package', value: upgrade.packageName },
              { label: 'Effective from', value: formatDate(upgrade.effectiveFrom) },
              {
                label: 'Stripe change',
                value: STRIPE_CHANGE_LABELS[upgrade.stripeChange] ?? upgrade.stripeChange,
                fullWidth: true,
              },
              { label: 'Recorded on month', value: formatPeriod(upgrade.stampedPeriod) },
            ]}
          />
        ) : (
          <Empty>
            No Enterprise+ transition has been recorded. Completing one records the agreed package
            and new allowances; it never changes the Stripe subscription.
          </Empty>
        )}
        <Divider sx={{ borderColor: '#EEF2F7' }} />
        <SubHeading>Contract variations ({variations.length})</SubHeading>
        {variations.length === 0 ? (
          <Empty>No contract variations have been recorded.</Empty>
        ) : (
          <Stack gap={1.25}>
            {visible.map((variation, index) => (
              <Box key={String(variation._id ?? index)}>
                <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                  <SoftChip
                    color={variation.kind === 'enterprisePlusTransition' ? '#239AA4' : '#64748B'}
                    label={VARIATION_KINDS[variation.kind] ?? variation.kind ?? 'Variation'}
                    sx={{ height: 20, fontSize: 11 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(variation.recordedAt)}
                    {variation.recordedByName ? ` · ${variation.recordedByName}` : ''}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}>
                  {variation.summary || '—'}
                </Typography>
              </Box>
            ))}
            {variations.length > VISIBLE_VARIATIONS && (
              <Button size="small" onClick={() => setShowAll(!showAll)} sx={{ alignSelf: 'flex-start' }}>
                {showAll ? 'Show fewer' : `Show all ${variations.length}`}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// 8. Action history
// ---------------------------------------------------------------------------

const VISIBLE_HISTORY = 12

export const HistoryCard = ({ panel }) => {
  const history = panel.history ?? []
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? history : history.slice(0, VISIBLE_HISTORY)

  return (
    <SectionCard
      title="Action history"
      icon={<HistoryIcon />}
      subtitle="Who did what, and when - newest first. Entries are permanent."
    >
      {history.length === 0 ? (
        <Empty>Nothing has been recorded yet.</Empty>
      ) : (
        <Stack>
          {visible.map((entry, index) => {
            const meta = historyActionMeta(entry.action)
            const system = entry.actorType === 'system'
            const actor = system
              ? entry.actorName
                ? `${entry.actorName} (automatic)`
                : 'Automatic'
              : entry.actorName
            return (
              <Stack
                key={`${entry.period}-${entry.at}-${entry.action}-${index}`}
                direction="row"
                gap={1.5}
                sx={{
                  position: 'relative',
                  pb: 2,
                  '&:not(:last-of-type)::before': {
                    content: '""',
                    position: 'absolute',
                    left: 11,
                    top: 24,
                    bottom: 0,
                    width: 2,
                    bgcolor: '#EEF2F7',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: alpha(meta.color, 0.15),
                    color: meta.color,
                    fontSize: 10,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                >
                  {system || !entry.actorName ? '•' : initials(entry.actorName)}
                </Box>
                <Box minWidth={0} flex={1}>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography
                      variant="caption"
                      sx={{ color: meta.color, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                    >
                      {meta.label}
                    </Typography>
                    <Typography variant="caption" color="text.primary" fontWeight={600}>
                      {formatPeriod(entry.period)}
                    </Typography>
                    {actor && (
                      <Typography variant="caption" color="text.secondary">
                        {actor}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {formatDateTime(entry.at)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {entry.detail}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
          {history.length > VISIBLE_HISTORY && (
            <Button size="small" onClick={() => setShowAll(!showAll)} sx={{ alignSelf: 'flex-start' }}>
              {showAll ? 'Show fewer' : `Show all ${history.length}`}
            </Button>
          )}
        </Stack>
      )}
    </SectionCard>
  )
}
